import { FpsOverlay } from "./fps_overlay.js";
import { Game } from "../game.js";
import { ScreenManager } from "../screen_manager/screen_manager.js";
import { UIElement } from "../ui/common_ui_elements/ui_element.js";

// Frame-by-frame profiler for the 2D canvas UI. The FPS overlay can only say
// that a spike was "out" (outside JS); this probe records what the frame drew
// (device pixels, shadows, filters, text strokes, first-time image uploads),
// which element drew it, which DOM canvases were composited and what the
// browser reported for the frame (long-animation-frame), so the outside-JS
// time can be tied to a cause. `bisect()` then re-runs the same screen with
// one suspect disabled at a time and compares the frame-time distributions.
//
// Console: uiPerf.rec(10) / uiPerf.bisect() / uiPerf.report() / uiPerf.panel()

type Knobs = {
  no_shadow: boolean;
  no_text_stroke: boolean;
  no_text: boolean;
  /** Skip drawImage onto a screen canvas that covers more than this share of it (0 = off). */
  skip_images_over: number;
  /** Cap on how often the current screen repaints (0 = uncapped). */
  repaint_hz: number;
};

const DEFAULT_KNOBS: Knobs = { no_shadow: false, no_text_stroke: false, no_text: false, skip_images_over: 0, repaint_hz: 0 };

type Frame = {
  t: number;
  dt: number;
  phys: number;
  game: number;
  ui: number;
  gl: number;
  screen: string;
  variant: string;
  /** Draw calls that landed on the visible screen canvas this frame. */
  scr_ops: number;
  img: number;
  img_mpx: number;
  rect_mpx: number;
  fills: number;
  text_fill: number;
  text_stroke: number;
  text_kpx: number;
  shadow: number;
  filter: number;
  off_ops: number;
  new_src: number;
  new_src_mpx: number;
  heap_mb: number;
  gc_mb: number;
  layers: string;
};

type ElementStat = { calls: number; self_ms: number; max_ms: number; img: number; img_mpx: number; text: number; stroke: number; shadow: number; filter: number };

type LoafScript = { invoker: string; fn: string; src: string; ms: number; layout_ms: number };
type Loaf = { start: number; end: number; ms: number; render_ms: number; layout_ms: number; blocking_ms: number; scripts: LoafScript[] };

type Summary = {
  variant: string;
  n: number;
  fps: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
  over25: number;
  over50: number;
  out_mean: number;
  ui_mean: number;
  repaint_pct: number;
  mpx_per_repaint: number;
};

const MPX = 1e6;
const MAX_FRAMES = 12000;
const SPIKE_MS = 25;
const REPORT_KEY = "bb_ui_perf_last_v1";

type HookedProto = Record<string, ( ...args: unknown[] ) => unknown>;
// Layer caches and card snapshots draw on OffscreenCanvas, whose context has its own prototype.
const CTX_PROTOS: HookedProto[] = [
  CanvasRenderingContext2D.prototype as unknown as HookedProto,
  ...( typeof OffscreenCanvasRenderingContext2D !== "undefined"
    ? [ OffscreenCanvasRenderingContext2D.prototype as unknown as HookedProto ]
    : [] ),
];
const HOOKED_METHODS = [ "drawImage", "fillRect", "fill", "stroke", "fillText", "strokeText", "getImageData", "putImageData" ] as const;

export class UiPerfProbe
{
  private static recording = false;
  private static detail = true;
  private static stop_at = 0;
  private static on_done: ( () => void ) | null = null;
  private static frames: Frame[] = [];
  private static loafs: Loaf[] = [];
  private static observer: PerformanceObserver | null = null;
  private static loaf_supported = false;
  private static prev_t = 0;
  private static prev_heap = 0;
  private static started_at = 0;
  private static variant = "base";
  private static skip_until = 0;
  private static bisecting = false;

  private static knobs: Knobs = { ...DEFAULT_KNOBS };
  private static originals: { proto: HookedProto; method: string; fn: ( ...args: unknown[] ) => unknown }[] = [];
  private static render_originals = new Map<object, () => void>();
  private static wrapped_root: { el: UIElement; render: () => void } | null = null;
  private static last_root_paint = 0;

  private static cur = UiPerfProbe.emptyCounters();
  private static seen_sources = new WeakSet<object>();
  private static element_stack: { key: string; t0: number; child_ms: number }[] = [];
  private static element_stats = new Map<string, ElementStat>();
  private static layers_snapshot = "";
  private static layers_checked_at = 0;
  private static last_wrap_walk = 0;
  private static screen_canvas: HTMLCanvasElement | null = null;

  private static panel_el: HTMLDivElement | null = null;
  private static panel_pre: HTMLPreElement | null = null;
  private static last_report = "";

  // ---- public API ----

  public static isRecording(): boolean { return this.recording; }

  public static rec( seconds = 10, detail = true ): void
  {
    this.begin( detail );
    this.stop_at = performance.now() + seconds * 1000;
    this.on_done = () => { this.end(); this.publish( this.buildReport() ); };
    this.status( `recording ${seconds}s on ${this.screenName()}…` );
  }

  private static armed = false;
  private static listening = false;

  /** Starts a detailed recording the moment the next screen is shown, so its opening frames are caught. */
  public static armOnNextScreen( seconds = 8 ): void
  {
    this.armed = true;
    if ( !this.listening )
    {
      this.listening = true;
      ScreenManager.addOnChangeListener( () =>
      {
        if ( !this.armed ) return;
        this.armed = false;
        this.rec( seconds );
      } );
    }
    this.status( `armed: recording starts when the next screen opens (${seconds}s)` );
  }

  public static stop(): string
  {
    if ( !this.recording ) return this.last_report;
    const was_bisect = this.bisecting;
    if ( was_bisect ) this.resetKnobs();
    this.end();
    const report = was_bisect
      ? this.buildBisectReport( [ ...new Set( this.frames.map( f => f.variant ) ) ] )
      : this.buildReport();
    this.publish( report );
    return report;
  }

  public static report(): string { return this.last_report || this.loadSaved(); }

  public static setKnobs( knobs: Partial<Knobs> ): Knobs
  {
    this.knobs = { ...this.knobs, ...knobs };
    this.applyRepaintCap();
    this.ensureHooks();
    return { ...this.knobs };
  }

  public static resetKnobs(): void
  {
    this.knobs = { ...DEFAULT_KNOBS };
    this.applyRepaintCap();
    if ( !this.recording ) this.removeHooks();
  }

  /**
   * Runs the current screen through a fixed list of variants, each with one
   * suspect removed, and prints the frame-time distribution of each. Lite
   * counters only, so the probe's own overhead stays equal across variants.
   */
  public static bisect( segment_s = 6 ): void
  {
    const variants: { name: string; knobs: Partial<Knobs> }[] = [
      { name: "base", knobs: {} },
      { name: "no_shadow", knobs: { no_shadow: true } },
      { name: "no_text_stroke", knobs: { no_text_stroke: true } },
      { name: "no_text", knobs: { no_text: true } },
      { name: "no_big_img(>25%)", knobs: { skip_images_over: 0.25 } },
      { name: "repaint_60hz", knobs: { repaint_hz: 60 } },
      { name: "repaint_30hz", knobs: { repaint_hz: 30 } },
      { name: "static(1hz)", knobs: { repaint_hz: 1 } },
      { name: "base_again", knobs: {} },
    ];

    this.begin( false );
    this.bisecting = true;
    const settle_ms = 1000;
    let index = -1;

    const next = (): void =>
    {
      index++;
      if ( index >= variants.length )
      {
        this.knobs = { ...DEFAULT_KNOBS };
        this.applyRepaintCap();
        this.end();
        this.publish( this.buildBisectReport( variants.map( v => v.name ) ) );
        return;
      }
      const v = variants[index];
      this.knobs = { ...DEFAULT_KNOBS, ...v.knobs };
      this.applyRepaintCap();
      // A static screen would otherwise keep showing the previous variant's pixels.
      this.currentRoot()?.forceMakeShouldBeRendered();
      this.variant = v.name;
      this.skip_until = performance.now() + settle_ms;
      this.stop_at = performance.now() + settle_ms + segment_s * 1000;
      this.on_done = next;
      this.status( `bisect ${index + 1}/${variants.length}: ${v.name} (${segment_s}s)… don't touch` );
    };

    next();
  }

  /** Game loop hook, once per rAF after FpsOverlay.tick(). */
  public static onFrame( now: number ): void
  {
    if ( !this.recording ) return;

    const dt = now - this.prev_t;
    this.prev_t = now;

    if ( this.detail && now - this.last_wrap_walk > 500 )
    {
      this.last_wrap_walk = now;
      this.wrapTree();
    }
    if ( now - this.layers_checked_at > 250 )
    {
      this.layers_checked_at = now;
      this.layers_snapshot = this.visibleLayers();
      this.screen_canvas = this.currentRoot()?.getContext().canvas ?? null;
    }

    const heap = UiPerfProbe.heapMb();
    const gc_mb = this.prev_heap > 0 && heap < this.prev_heap ? this.prev_heap - heap : 0;
    this.prev_heap = heap;

    if ( dt > 0 && dt < 4000 && now >= this.skip_until && this.frames.length < MAX_FRAMES )
    {
      const s = FpsOverlay.lastSections();
      const c = this.cur;
      this.frames.push( {
        t: now - this.started_at,
        dt,
        phys: s.phys,
        game: s.game,
        ui: s.ui2d,
        gl: s.gl,
        screen: this.screenName(),
        variant: this.variant,
        scr_ops: c.scr_ops,
        img: c.img,
        img_mpx: c.img_mpx,
        rect_mpx: c.rect_mpx,
        fills: c.fills,
        text_fill: c.text_fill,
        text_stroke: c.text_stroke,
        text_kpx: c.text_kpx,
        shadow: c.shadow,
        filter: c.filter,
        off_ops: c.off_ops,
        new_src: c.new_src,
        new_src_mpx: c.new_src_mpx,
        heap_mb: heap,
        gc_mb,
        layers: this.layers_snapshot,
      } );
    }
    this.cur = UiPerfProbe.emptyCounters();

    if ( now >= this.stop_at && this.on_done )
    {
      const done = this.on_done;
      this.on_done = null;
      done();
    }
  }

  // ---- lifecycle ----

  private static begin( detail: boolean ): void
  {
    if ( this.recording ) this.end();
    this.recording = true;
    this.detail = detail;
    this.frames = [];
    this.loafs = [];
    this.element_stats.clear();
    this.element_stack = [];
    this.seen_sources = new WeakSet();
    this.cur = UiPerfProbe.emptyCounters();
    this.variant = "base";
    this.skip_until = 0;
    this.started_at = performance.now();
    this.prev_t = this.started_at;
    this.prev_heap = 0;
    this.layers_snapshot = this.visibleLayers();
    this.screen_canvas = this.currentRoot()?.getContext().canvas ?? null;
    FpsOverlay.force_marks = true;
    this.ensureHooks();
    if ( detail ) this.wrapTree();
    this.loaf_supported = this.observeLoaf();
  }

  private static end(): void
  {
    this.recording = false;
    this.bisecting = false;
    this.on_done = null;
    FpsOverlay.force_marks = false;
    this.observer?.disconnect();
    this.observer = null;
    this.unwrapTree();
    const knobs_active = JSON.stringify( this.knobs ) !== JSON.stringify( DEFAULT_KNOBS );
    if ( !knobs_active ) this.removeHooks();
  }

  // ---- canvas hooks ----

  private static emptyCounters()
  {
    return { scr_ops: 0, img: 0, img_mpx: 0, rect_mpx: 0, fills: 0, text_fill: 0, text_stroke: 0, text_kpx: 0, shadow: 0, filter: 0, off_ops: 0, new_src: 0, new_src_mpx: 0 };
  }

  private static ensureHooks(): void
  {
    if ( this.originals.length ) return;

    for ( const proto of CTX_PROTOS )
      for ( const method of HOOKED_METHODS )
      {
        const original = proto[method];
        if ( typeof original !== "function" ) continue;
        this.originals.push( { proto, method, fn: original } );
        proto[method] = function ( this: CanvasRenderingContext2D, ...args: unknown[] )
        {
          return UiPerfProbe.onCanvasOp( this, method, args, original );
        };
      }
  }

  private static removeHooks(): void
  {
    for ( const { proto, method, fn } of this.originals )
      proto[method] = fn;
    this.originals = [];
  }

  private static onCanvasOp(
    ctx: CanvasRenderingContext2D,
    method: typeof HOOKED_METHODS[number],
    args: unknown[],
    original: ( ...args: unknown[] ) => unknown
  ): unknown
  {
    const canvas = ctx.canvas;
    const on_screen = canvas === this.screen_canvas;
    const in_dom = on_screen || ( canvas as HTMLCanvasElement ).isConnected === true;
    const knobs = this.knobs;
    const is_text = method === "fillText" || method === "strokeText";

    // ---- knobs (only on visible canvases; offscreen caches are left intact) ----
    if ( in_dom && ( canvas as HTMLCanvasElement ).id !== "fpsCanvas" )
    {
      if ( is_text && knobs.no_text ) return undefined;
      if ( method === "strokeText" && knobs.no_text_stroke ) return undefined;
      if ( method === "drawImage" && knobs.skip_images_over > 0
        && UiPerfProbe.cheapDestArea( args ) > knobs.skip_images_over * canvas.width * canvas.height )
        return undefined;
    }

    if ( this.recording )
      this.count( ctx, canvas, on_screen, in_dom, method, args );

    if ( in_dom && knobs.no_shadow && ctx.shadowBlur > 0 )
    {
      const blur = ctx.shadowBlur;
      ctx.shadowBlur = 0;
      const out = original.apply( ctx, args );
      ctx.shadowBlur = blur;
      return out;
    }
    return original.apply( ctx, args );
  }

  private static count(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement | OffscreenCanvas,
    on_screen: boolean,
    in_dom: boolean,
    method: string,
    args: unknown[]
  ): void
  {
    if ( canvas instanceof HTMLCanvasElement && canvas.id === "fpsCanvas" ) return;

    const c = this.cur;
    if ( !in_dom )
    {
      c.off_ops++;
      return;
    }
    if ( on_screen ) c.scr_ops++;

    const shadow = ctx.shadowBlur > 0 && ctx.shadowColor !== "rgba(0, 0, 0, 0)" && ctx.shadowColor !== "transparent";
    const filter = ctx.filter !== "none" && ctx.filter !== "";
    if ( shadow ) c.shadow++;
    if ( filter ) c.filter++;

    let img_mpx = 0;
    let is_img = false;
    let is_text = false;
    let is_stroke = false;

    if ( method === "drawImage" )
    {
      is_img = true;
      c.img++;
      // Through the real transform in both modes: a cached layer is blitted
      // with an identity transform, which the cheap estimate overcounts by dpr².
      img_mpx = this.deviceArea( ctx, canvas, args ) / MPX;
      c.img_mpx += img_mpx;

      const src = args[0] as { width?: number; height?: number } | null;
      if ( src && typeof src === "object" && !this.seen_sources.has( src ) )
      {
        this.seen_sources.add( src );
        const src_mpx = ( src.width ?? 0 ) * ( src.height ?? 0 ) / MPX;
        if ( src_mpx > 0.05 )
        {
          c.new_src++;
          c.new_src_mpx += src_mpx;
        }
      }
    }
    else if ( method === "fillRect" )
    {
      c.rect_mpx += this.detail ? this.deviceArea( ctx, canvas, [ null, args[0], args[1], args[2], args[3] ] ) / MPX : 0;
    }
    else if ( method === "fill" || method === "stroke" )
    {
      c.fills++;
    }
    else if ( method === "fillText" || method === "strokeText" )
    {
      is_text = true;
      is_stroke = method === "strokeText";
      if ( is_stroke ) c.text_stroke++; else c.text_fill++;
      const text = String( args[0] ?? "" );
      const font_px = UiPerfProbe.fontPx( ctx.font ) * Game.getDpr();
      c.text_kpx += text.length * font_px * font_px * 0.6 / 1000;
    }

    if ( !this.detail ) return;
    const top = this.element_stack[this.element_stack.length - 1];
    if ( !top ) return;
    const stat = this.stat( top.key );
    if ( is_img ) { stat.img++; stat.img_mpx += img_mpx; }
    if ( is_text ) stat.text++;
    if ( is_stroke ) stat.stroke++;
    if ( shadow ) stat.shadow++;
    if ( filter ) stat.filter++;
  }

  private static fontPx( font: string ): number
  {
    const match = /(\d+(?:\.\d+)?)px/.exec( font );
    return match ? parseFloat( match[1] ) : 16;
  }

  /** Destination size in CSS px times dpr² — good enough when no scale transform is active. */
  private static cheapDestArea( args: unknown[] ): number
  {
    const dpr = Game.getDpr();
    let w = 0;
    let h = 0;
    if ( args.length >= 9 ) { w = args[7] as number; h = args[8] as number; }
    else if ( args.length >= 5 ) { w = args[3] as number; h = args[4] as number; }
    else
    {
      const src = args[0] as { width?: number; height?: number };
      w = src?.width ?? 0;
      h = src?.height ?? 0;
    }
    return Math.abs( w * h ) * dpr * dpr;
  }

  /** Destination rect through the current transform, clipped to the canvas, in device px. */
  private static deviceArea( ctx: CanvasRenderingContext2D, canvas: { width: number; height: number }, args: unknown[] ): number
  {
    let x = 0, y = 0, w = 0, h = 0;
    if ( args.length >= 9 ) { x = args[5] as number; y = args[6] as number; w = args[7] as number; h = args[8] as number; }
    else if ( args.length >= 5 ) { x = args[1] as number; y = args[2] as number; w = args[3] as number; h = args[4] as number; }
    else
    {
      const src = args[0] as { width?: number; height?: number };
      x = args[1] as number; y = args[2] as number; w = src?.width ?? 0; h = src?.height ?? 0;
    }
    const m = ctx.getTransform();
    const xs = [ x, x + w, x, x + w ];
    const ys = [ y, y, y + h, y + h ];
    let min_x = Infinity, min_y = Infinity, max_x = -Infinity, max_y = -Infinity;
    for ( let i = 0; i < 4; i++ )
    {
      const px = m.a * xs[i] + m.c * ys[i] + m.e;
      const py = m.b * xs[i] + m.d * ys[i] + m.f;
      if ( px < min_x ) min_x = px;
      if ( px > max_x ) max_x = px;
      if ( py < min_y ) min_y = py;
      if ( py > max_y ) max_y = py;
    }
    const cw = Math.max( 0, Math.min( max_x, canvas.width ) - Math.max( min_x, 0 ) );
    const ch = Math.max( 0, Math.min( max_y, canvas.height ) - Math.max( min_y, 0 ) );
    return cw * ch;
  }

  // ---- element attribution ----

  private static stat( key: string ): ElementStat
  {
    let stat = this.element_stats.get( key );
    if ( !stat )
    {
      stat = { calls: 0, self_ms: 0, max_ms: 0, img: 0, img_mpx: 0, text: 0, stroke: 0, shadow: 0, filter: 0 };
      this.element_stats.set( key, stat );
    }
    return stat;
  }

  private static currentRoot(): UIElement | null
  {
    const screen = ScreenManager.getCurrentScreen();
    return ( Game.instance?.currentScreenUi( screen ) as UIElement | null ) ?? null;
  }

  private static screenName(): string
  {
    return String( ScreenManager.getCurrentScreen() ?? "none" );
  }

  /** Wraps render() on every prototype found in the current screen tree, once. */
  private static wrapTree(): void
  {
    const root = this.currentRoot();
    if ( !root ) return;

    const visit = ( el: UIElement ): void =>
    {
      let proto = Object.getPrototypeOf( el ) as Record<string, unknown> | null;
      while ( proto && proto !== ( UIElement.prototype as unknown ) && proto !== Object.prototype )
      {
        if ( Object.prototype.hasOwnProperty.call( proto, "render" ) && !this.render_originals.has( proto ) )
        {
          const original = proto.render as () => void;
          this.render_originals.set( proto, original );
          proto.render = function ( this: UIElement ): void
          {
            UiPerfProbe.enterElement( this );
            try { original.call( this ); }
            finally { UiPerfProbe.leaveElement(); }
          };
        }
        proto = Object.getPrototypeOf( proto ) as Record<string, unknown> | null;
      }
      for ( const child of el.getChildren() )
        visit( child );
    };
    visit( root );
  }

  private static unwrapTree(): void
  {
    for ( const [ proto, original ] of this.render_originals )
      ( proto as Record<string, unknown> ).render = original;
    this.render_originals.clear();
    this.element_stack = [];
  }

  private static enterElement( el: UIElement ): void
  {
    if ( !el.shouldBeRendered() )
    {
      // A clean element still gets draws from its own render() when it paints
      // a cached layer itself (offer screens), so keep a named bucket.
      this.element_stack.push( { key: `${el.name()} (clean/cache)`, t0: -1, child_ms: 0 } );
      return;
    }
    const key = UiPerfProbe.elementKey( el );
    this.element_stack.push( { key, t0: performance.now(), child_ms: 0 } );
  }

  private static leaveElement(): void
  {
    const frame = this.element_stack.pop();
    if ( !frame || frame.t0 < 0 ) return;
    const total = performance.now() - frame.t0;
    const stat = this.stat( frame.key );
    const self_ms = total - frame.child_ms;
    stat.calls++;
    stat.self_ms += self_ms;
    if ( self_ms > stat.max_ms ) stat.max_ms = self_ms;
    const parent = this.element_stack[this.element_stack.length - 1];
    if ( parent ) parent.child_ms += total;
  }

  private static elementKey( el: UIElement ): string
  {
    const cls = el.constructor?.name || "?";
    const name = el.name();
    const rect = el.getRect();
    const size = `${Math.round( rect.getW() )}x${Math.round( rect.getH() )}`;
    const extra = UiPerfProbe.elementDetail( el );
    return `${name === cls ? cls : `${cls}:${name}`} ${size}${extra}`;
  }

  private static elementDetail( el: UIElement ): string
  {
    const any_el = el as unknown as { getImage?: () => { width: number; height: number } | null; text?: string; localization_key?: string };
    if ( typeof any_el.getImage === "function" )
    {
      const image = any_el.getImage();
      return image ? ` src${image.width}x${image.height}` : " src-";
    }
    const text = any_el.text ?? any_el.localization_key;
    return typeof text === "string" && text.length ? ` "${text.slice( 0, 18 )}"` : "";
  }

  // ---- repaint cap ----

  private static applyRepaintCap(): void
  {
    const root = this.currentRoot();

    if ( this.wrapped_root && ( this.knobs.repaint_hz === 0 || this.wrapped_root.el !== root ) )
    {
      delete ( this.wrapped_root.el as unknown as Record<string, unknown> ).render;
      this.wrapped_root = null;
    }
    if ( this.knobs.repaint_hz === 0 || !root || this.wrapped_root ) return;

    const proto_render = Object.getPrototypeOf( root ).render as () => void;
    const el = root as unknown as Record<string, unknown>;
    el.render = function ( this: UIElement ): void
    {
      const now = performance.now();
      const hz = UiPerfProbe.knobs.repaint_hz;
      // Skipping leaves the dirty flags set, so the next allowed frame paints it all.
      if ( hz > 0 && now - UiPerfProbe.last_root_paint < 1000 / hz - 1 ) return;
      if ( this.shouldBeRendered() ) UiPerfProbe.last_root_paint = now;
      proto_render.call( this );
    };
    this.wrapped_root = { el: root, render: proto_render };
  }

  // ---- environment ----

  private static visibleLayers(): string
  {
    const parts: string[] = [];
    const list = document.getElementsByTagName( "canvas" );
    for ( let i = 0; i < list.length; i++ )
    {
      const canvas = list[i];
      if ( canvas.id === "fpsCanvas" || canvas.style.display === "none" || !canvas.isConnected ) continue;
      if ( canvas.parentElement && canvas.parentElement.style.display === "none" ) continue;
      parts.push( `${canvas.id || "?"}(${( canvas.width * canvas.height / MPX ).toFixed( 1 )})` );
    }
    return parts.join( " " );
  }

  private static heapMb(): number
  {
    const memory = ( performance as unknown as { memory?: { usedJSHeapSize: number } } ).memory;
    return memory ? memory.usedJSHeapSize / 1048576 : 0;
  }

  private static observeLoaf(): boolean
  {
    const supported = ( PerformanceObserver as unknown as { supportedEntryTypes?: readonly string[] } ).supportedEntryTypes ?? [];
    const type = supported.includes( "long-animation-frame" ) ? "long-animation-frame" : supported.includes( "longtask" ) ? "longtask" : null;
    if ( !type ) return false;

    try
    {
      this.observer = new PerformanceObserver( list =>
      {
        for ( const raw of list.getEntries() )
        {
          if ( this.loafs.length >= 2000 ) return;
          const entry = raw as PerformanceEntry & {
            renderStart?: number; styleAndLayoutStart?: number; blockingDuration?: number;
            scripts?: { invoker?: string; sourceFunctionName?: string; sourceURL?: string; duration: number; forcedStyleAndLayoutDuration?: number }[];
          };
          const end = entry.startTime + entry.duration;
          const render_start = entry.renderStart ?? 0;
          const layout_start = entry.styleAndLayoutStart ?? 0;
          this.loafs.push( {
            start: entry.startTime - this.started_at,
            end: end - this.started_at,
            ms: entry.duration,
            render_ms: render_start > 0 ? end - render_start : 0,
            layout_ms: layout_start > 0 ? end - layout_start : 0,
            blocking_ms: entry.blockingDuration ?? 0,
            scripts: ( entry.scripts ?? [] ).map( s => ( {
              invoker: s.invoker ?? "",
              fn: s.sourceFunctionName ?? "",
              src: ( s.sourceURL ?? "" ).split( "/" ).pop()?.split( "?" )[0] ?? "",
              ms: s.duration,
              layout_ms: s.forcedStyleAndLayoutDuration ?? 0,
            } ) ),
          } );
        }
      } );
      this.observer.observe( { type, buffered: false } );
      return true;
    }
    catch
    {
      this.observer = null;
      return false;
    }
  }

  // ---- reports ----

  private static quantile( sorted: number[], q: number ): number
  {
    if ( !sorted.length ) return 0;
    return sorted[Math.min( sorted.length - 1, Math.floor( ( sorted.length - 1 ) * q ) )];
  }

  private static summarize( frames: Frame[], variant: string ): Summary
  {
    const dts = frames.map( f => f.dt ).sort( ( a, b ) => a - b );
    const n = frames.length;
    const total = dts.reduce( ( a, b ) => a + b, 0 );
    const repaint = frames.filter( f => f.scr_ops > 0 );
    const mean = ( pick: ( f: Frame ) => number, list = frames ) => list.length ? list.reduce( ( a, f ) => a + pick( f ), 0 ) / list.length : 0;
    return {
      variant,
      n,
      fps: total > 0 ? 1000 * n / total : 0,
      p50: this.quantile( dts, 0.5 ),
      p95: this.quantile( dts, 0.95 ),
      p99: this.quantile( dts, 0.99 ),
      max: dts[n - 1] ?? 0,
      over25: frames.filter( f => f.dt >= 25 ).length,
      over50: frames.filter( f => f.dt >= 50 ).length,
      out_mean: mean( f => Math.max( 0, f.dt - f.phys - f.game - f.ui - f.gl ) ),
      ui_mean: mean( f => f.ui ),
      repaint_pct: n ? 100 * repaint.length / n : 0,
      mpx_per_repaint: mean( f => f.img_mpx + f.rect_mpx, repaint ),
    };
  }

  private static fmtSummaryHeader(): string
  {
    return "variant            frames  fps   p50   p95   p99   max  >25 >50  out  ui  rep%  Mpx/rep";
  }

  private static fmtSummary( s: Summary ): string
  {
    const pad = ( v: string, n: number ) => v.length >= n ? v.slice( 0, n ) : v + " ".repeat( n - v.length );
    const num = ( v: number, n: number, d = 1 ) => { const t = v.toFixed( d ); return " ".repeat( Math.max( 0, n - t.length ) ) + t; };
    return `${pad( s.variant, 18 )} ${num( s.n, 6, 0 )} ${num( s.fps, 5 )} ${num( s.p50, 5 )} ${num( s.p95, 5 )} ${num( s.p99, 5 )} ${num( s.max, 5, 0 )} ${num( s.over25, 4, 0 )} ${num( s.over50, 3, 0 )} ${num( s.out_mean, 4 )} ${num( s.ui_mean, 3 )} ${num( s.repaint_pct, 5, 0 )} ${num( s.mpx_per_repaint, 7 )}`;
  }

  private static header(): string[]
  {
    const dpr = Game.getDpr();
    return [
      `UIPERF ${new Date().toISOString()}  ua=${navigator.userAgent.replace( /^.*?\(/, "(" ).slice( 0, 90 )}`,
      `screen=${this.screenName()} css=${Game.getScreenWidth()}x${Game.getScreenHeight()} dpr=${dpr.toFixed( 2 )} `
        + `canvas=${( Game.getScreenWidth() * Game.getScreenHeight() * dpr * dpr / MPX ).toFixed( 2 )}Mpx `
        + `loaf=${this.loaf_supported ? "yes" : "no"} heap_api=${this.heapMb() > 0 ? "yes" : "no"}`,
      `layers: ${this.layers_snapshot || "-"}`,
      ...this.canvasInventory(),
    ];
  }

  private static buildReport(): string
  {
    const frames = this.frames;
    if ( !frames.length ) return "UIPERF: no frames recorded";

    const lines = this.header();
    const screen_mpx = Game.getScreenWidth() * Game.getScreenHeight() * Game.getDpr() ** 2 / MPX;
    const vsync = this.quantile( frames.map( f => f.dt ).sort( ( a, b ) => a - b ), 0.1 );
    lines.push( `vsync≈${vsync.toFixed( 1 )}ms (${( 1000 / Math.max( 1, vsync ) ).toFixed( 0 )}Hz)` );

    lines.push( "", this.fmtSummaryHeader() );
    const by_screen = new Map<string, Frame[]>();
    for ( const f of frames )
    {
      const list = by_screen.get( f.screen ) ?? [];
      list.push( f );
      by_screen.set( f.screen, list );
    }
    for ( const [ screen, list ] of by_screen )
      lines.push( this.fmtSummary( this.summarize( list, screen ) ) );

    // Repaint vs idle frames: tells whether the cost follows what the UI draws.
    const repaint = frames.filter( f => f.scr_ops > 0 );
    const idle = frames.filter( f => f.scr_ops === 0 );
    const mean = ( list: Frame[], pick: ( f: Frame ) => number ) => list.length ? list.reduce( ( a, f ) => a + pick( f ), 0 ) / list.length : 0;
    const out = ( f: Frame ) => Math.max( 0, f.dt - f.phys - f.game - f.ui - f.gl );
    lines.push( "",
      `REPAINT frames=${repaint.length} dt=${mean( repaint, f => f.dt ).toFixed( 1 )} out=${mean( repaint, out ).toFixed( 1 )} ui=${mean( repaint, f => f.ui ).toFixed( 1 )} `
        + `spikes>25=${repaint.filter( f => f.dt >= SPIKE_MS ).length}`,
      `IDLE    frames=${idle.length} dt=${mean( idle, f => f.dt ).toFixed( 1 )} out=${mean( idle, out ).toFixed( 1 )} spikes>25=${idle.filter( f => f.dt >= SPIKE_MS ).length}`,
      `per repaint: img=${mean( repaint, f => f.img ).toFixed( 0 )} img=${mean( repaint, f => f.img_mpx ).toFixed( 1 )}Mpx `
        + `(overdraw ${( mean( repaint, f => f.img_mpx + f.rect_mpx ) / screen_mpx ).toFixed( 1 )}x screen) fills=${mean( repaint, f => f.fills ).toFixed( 0 )} `
        + `text=${mean( repaint, f => f.text_fill ).toFixed( 0 )}+${mean( repaint, f => f.text_stroke ).toFixed( 0 )}stroke `
        + `shadow=${mean( repaint, f => f.shadow ).toFixed( 0 )} filter=${mean( repaint, f => f.filter ).toFixed( 0 )} offscreen_ops=${mean( frames, f => f.off_ops ).toFixed( 1 )}`,
    );

    // Correlation of dt with the drawn workload — a GPU-fill-bound screen shows it on img Mpx.
    lines.push( `corr(dt, img_mpx)=${this.corr( frames, f => f.img_mpx ).toFixed( 2 )} corr(dt, text)=${this.corr( frames, f => f.text_fill + f.text_stroke ).toFixed( 2 )} `
      + `corr(dt, shadow)=${this.corr( frames, f => f.shadow ).toFixed( 2 )} corr(dt, gc)=${this.corr( frames, f => f.gc_mb ).toFixed( 2 )}` );

    const gc_frames = frames.filter( f => f.gc_mb > 0.5 );
    lines.push( `GC drops>0.5MB: ${gc_frames.length}, of them spikes>25: ${gc_frames.filter( f => f.dt >= SPIKE_MS ).length}` );

    // Elements, heaviest first.
    if ( this.element_stats.size )
    {
      // Per render of that element: backdrop layers and cards paint on their
      // own schedule, so dividing by the screen canvas' repaints misleads.
      const rows = [ ...this.element_stats.entries() ]
        .map( ( [ key, s ] ) => ( { key, ...s, score: s.self_ms + s.img_mpx * 0.5 + s.stroke * 0.2 + s.shadow * 0.5 } ) )
        .sort( ( a, b ) => b.score - a.score )
        .slice( 0, 18 );
      lines.push( "", "TOP ELEMENTS (per render of the element)            renders avg_ms max_ms img_Mpx txt/str shadow filter" );
      for ( const r of rows )
      {
        const n = Math.max( 1, r.calls );
        lines.push( `  ${r.key.slice( 0, 50 ).padEnd( 50 )} ${String( r.calls ).padStart( 7 )} ${( r.self_ms / n ).toFixed( 2 ).padStart( 6 )} ${r.max_ms.toFixed( 1 ).padStart( 6 )} `
          + `${( r.img_mpx / n ).toFixed( 2 ).padStart( 7 )} ${( r.text / n ).toFixed( 1 )}/${( r.stroke / n ).toFixed( 1 )} ${( r.shadow / n ).toFixed( 1 ).padStart( 6 )} ${( r.filter / n ).toFixed( 1 ).padStart( 6 )}` );
      }
    }

    // Spikes with everything known about their frame.
    const spikes = frames.map( ( f, i ) => ( { f, i } ) ).filter( x => x.f.dt >= SPIKE_MS ).sort( ( a, b ) => b.f.dt - a.f.dt ).slice( 0, 15 );
    lines.push( "", `SPIKES >=${SPIKE_MS}ms: ${frames.filter( f => f.dt >= SPIKE_MS ).length} (top ${spikes.length})` );
    lines.push( "  t_s     dt  js  out rep  img Mpx txt/str shd new_src   gc  loaf" );
    for ( const { f, i } of spikes )
    {
      const js = f.phys + f.game + f.ui + f.gl;
      const prev = frames[i - 1];
      const loaf = this.loafFor( f );
      lines.push(
        `  ${( f.t / 1000 ).toFixed( 2 ).padStart( 6 )} ${f.dt.toFixed( 0 ).padStart( 4 )} ${js.toFixed( 0 ).padStart( 3 )} ${( f.dt - js ).toFixed( 0 ).padStart( 4 )} `
          + `${f.scr_ops > 0 ? "Y" : "n"}/${prev && prev.scr_ops > 0 ? "Y" : "n"} ${String( f.img ).padStart( 3 )} ${f.img_mpx.toFixed( 1 ).padStart( 4 )} `
          + `${f.text_fill}/${f.text_stroke} ${String( f.shadow ).padStart( 3 )} ${f.new_src ? `${f.new_src}:${f.new_src_mpx.toFixed( 1 )}M` : "-"} `
          + `${f.gc_mb > 0.05 ? f.gc_mb.toFixed( 1 ) : "-"} ${loaf}`
          + ( f.screen !== ( prev?.screen ?? f.screen ) ? ` SCREEN->${f.screen}` : "" )
          + ( f.layers !== ( prev?.layers ?? f.layers ) ? ` LAYERS:${f.layers}` : "" ),
      );
    }
    lines.push( "  rep = screen canvas repainted this/prev frame; out = dt - JS (raster/GPU/compositor/GC/other tasks)" );

    const scripts = this.topLoafScripts();
    if ( scripts.length )
      lines.push( "", "LONG-FRAME SCRIPTS (sum ms, count):", ...scripts.map( s => `  ${s}` ) );

    return lines.join( "\n" );
  }

  private static buildBisectReport( order: string[] ): string
  {
    const lines = this.header();
    lines.push( "", "BISECT (same screen, one suspect removed per row; compare p95/p99/>25/out with base)", this.fmtSummaryHeader() );
    const base = this.summarize( this.frames.filter( f => f.variant === "base" ), "base" );
    for ( const name of order )
    {
      const list = this.frames.filter( f => f.variant === name );
      if ( !list.length ) continue;
      const s = this.summarize( list, name );
      const gain = base.p99 > 0 ? ` (p99 ${( ( 1 - s.p99 / base.p99 ) * 100 ).toFixed( 0 )}%)` : "";
      lines.push( this.fmtSummary( s ) + ( name === "base" ? "" : gain ) );
    }
    lines.push( "  >25/>50 = frames slower than 25/50 ms; out = mean dt minus JS; rep% = frames the screen canvas repainted" );
    return lines.join( "\n" );
  }

  private static corr( frames: Frame[], pick: ( f: Frame ) => number ): number
  {
    const n = frames.length;
    if ( n < 3 ) return 0;
    let sx = 0, sy = 0, sxx = 0, syy = 0, sxy = 0;
    for ( const f of frames )
    {
      const x = pick( f ), y = f.dt;
      sx += x; sy += y; sxx += x * x; syy += y * y; sxy += x * y;
    }
    const cov = sxy / n - ( sx / n ) * ( sy / n );
    const vx = sxx / n - ( sx / n ) ** 2;
    const vy = syy / n - ( sy / n ) ** 2;
    return vx > 0 && vy > 0 ? cov / Math.sqrt( vx * vy ) : 0;
  }

  private static loafFor( f: Frame ): string
  {
    const from = f.t - f.dt - 2;
    const hits = this.loafs.filter( l => l.end > from && l.start < f.t + 2 );
    if ( !hits.length ) return "-";
    return hits.map( l =>
    {
      const top = [ ...l.scripts ].sort( ( a, b ) => b.ms - a.ms )[0];
      const script = top ? ` ${top.invoker || top.fn}${top.src ? `@${top.src}` : ""}:${top.ms.toFixed( 0 )}` : "";
      return `[${l.ms.toFixed( 0 )} rnd${l.render_ms.toFixed( 0 )} lay${l.layout_ms.toFixed( 0 )}${script}]`;
    } ).join( "" );
  }

  private static topLoafScripts(): string[]
  {
    const agg = new Map<string, { ms: number; n: number }>();
    for ( const l of this.loafs )
      for ( const s of l.scripts )
      {
        const key = `${s.invoker || "?"} ${s.fn}${s.src ? `@${s.src}` : ""}`;
        const row = agg.get( key ) ?? { ms: 0, n: 0 };
        row.ms += s.ms;
        row.n++;
        agg.set( key, row );
      }
    return [ ...agg.entries() ].sort( ( a, b ) => b[1].ms - a[1].ms ).slice( 0, 8 )
      .map( ( [ key, v ] ) => `${v.ms.toFixed( 0 ).padStart( 6 )} ${String( v.n ).padStart( 4 )}  ${key.slice( 0, 80 )}` );
  }

  // ---- output ----

  private static publish( report: string ): void
  {
    this.last_report = report;
    try { localStorage.setItem( REPORT_KEY, report ); }
    catch {}
    console.log( `[UIPERF]\n${report}` );
    this.panel();
    if ( this.panel_pre ) this.panel_pre.textContent = report;
  }

  private static loadSaved(): string
  {
    try { return localStorage.getItem( REPORT_KEY ) ?? ""; }
    catch { return ""; }
  }

  private static status( text: string ): void
  {
    console.log( `[UIPERF] ${text}` );
    if ( this.panel_pre ) this.panel_pre.textContent = text;
  }

  public static isPanelVisible(): boolean { return this.panel_el !== null; }

  public static togglePanel(): void
  {
    if ( this.panel_el ) { this.panel_el.remove(); this.panel_el = null; this.panel_pre = null; }
    else this.panel();
  }

  /** Small floating panel with record/bisect/copy, so the probe runs on a phone without devtools. */
  public static panel(): void
  {
    if ( this.panel_el ) return;

    const box = document.createElement( "div" );
    box.style.cssText = "position:fixed;left:4px;right:4px;bottom:4px;max-height:46vh;z-index:100000;background:rgba(0,0,0,0.86);"
      + "color:#cfe;font:10px/1.25 monospace;border:1px solid #4af;border-radius:6px;display:flex;flex-direction:column;";

    const bar = document.createElement( "div" );
    bar.style.cssText = "display:flex;gap:4px;padding:4px;flex-wrap:wrap;";
    const button = ( label: string, action: () => void ) =>
    {
      const b = document.createElement( "button" );
      b.textContent = label;
      b.style.cssText = "font:bold 11px monospace;padding:6px 8px;background:#135;color:#fff;border:1px solid #4af;border-radius:4px;";
      b.addEventListener( "click", ev => { ev.stopPropagation(); action(); } );
      bar.appendChild( b );
    };
    button( "REC 10s", () => this.rec( 10 ) );
    button( "REC NEXT SCREEN", () => this.armOnNextScreen( 8 ) );
    button( "CANVAS BENCH", () => { this.status( "bench running…" ); setTimeout( () => this.canvasBench(), 50 ); } );
    button( "BISECT", () => this.bisect() );
    button( "COPY", () => void this.copy() );
    button( "LAST", () => { if ( this.panel_pre ) this.panel_pre.textContent = this.report() || "no report yet"; } );
    button( "X", () => this.togglePanel() );

    const pre = document.createElement( "pre" );
    pre.style.cssText = "margin:0;padding:4px 6px;overflow:auto;white-space:pre;flex:1;user-select:text;-webkit-user-select:text;";
    pre.textContent = this.last_report || "Open the screen, then REC 10s (detail) or BISECT (~70s, don't touch).";

    // The game listens on the canvases; keep panel touches from reaching them.
    for ( const type of [ "touchstart", "touchend", "pointerdown", "pointerup", "mousedown", "mouseup" ] )
      box.addEventListener( type, ev => ev.stopPropagation() );

    box.appendChild( bar );
    box.appendChild( pre );
    document.body.appendChild( box );
    this.panel_el = box;
    this.panel_pre = pre;
  }

  // ---- canvas bench ----

  /** Every canvas in the document with its backing size, hidden ones included (they keep their GPU memory). */
  private static canvasInventory(): string[]
  {
    const list = document.getElementsByTagName( "canvas" );
    let total = 0;
    let hidden_total = 0;
    const rows: string[] = [];
    for ( let i = 0; i < list.length; i++ )
    {
      const c = list[i];
      const mpx = c.width * c.height / MPX;
      const hidden = c.style.display === "none";
      total += mpx;
      if ( hidden ) hidden_total += mpx;
      if ( mpx >= 0.5 ) rows.push( `${c.id || "?"}:${mpx.toFixed( 1 )}${hidden ? "h" : ""}` );
    }
    return [
      `canvases in DOM: ${list.length}, total ${total.toFixed( 1 )} Mpx (~${( total * 4 ).toFixed( 0 )} MB), hidden ${hidden_total.toFixed( 1 )} Mpx`,
      `  ${rows.join( " " )}`,
    ];
  }

  private static paintHeavy( ctx: CanvasRenderingContext2D, w: number, h: number, scale: number ): void
  {
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.clearRect( 0, 0, w, h );
    const gradient = ctx.createLinearGradient( 0, 0, 0, h );
    gradient.addColorStop( 0, "#4E5BD4" );
    gradient.addColorStop( 1, "#C66026" );
    ctx.fillStyle = gradient;
    ctx.fillRect( 0, 0, w, h );

    const unit = w / 1080;
    for ( let i = 0; i < 3; i++ )
    {
      ctx.save();
      ctx.shadowColor = "rgba(255,255,255,0.7)";
      ctx.shadowBlur = 6 * unit;
      ctx.fillStyle = "#56148F";
      ctx.beginPath();
      ctx.roundRect( ( 30 + i * 350 ) * unit, 700 * unit, 320 * unit, 1100 * unit, 50 * unit );
      ctx.fill();
      ctx.restore();
    }

    ctx.translate( w / 2, h / 3 );
    ctx.scale( scale, scale );
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.font = `900 ${Math.round( 130 * unit )}px Geologica, sans-serif`;
    for ( const width of [ 42, 38, 28, 22, 6 ] )
    {
      ctx.lineWidth = width * unit;
      ctx.strokeStyle = "#6C1F04";
      ctx.strokeText( "Свято 1+2", 0, 0 );
    }
    ctx.fillStyle = "#FCEA3E";
    ctx.fillText( "Свято 1+2", 0, 0 );
    ctx.font = `800 ${Math.round( 59 * unit )}px Geologica, sans-serif`;
    for ( let i = 0; i < 12; i++ )
    {
      ctx.lineWidth = 9 * unit;
      ctx.strokeText( "2000", ( i % 3 - 1 ) * 330 * unit, ( 300 + Math.floor( i / 3 ) * 220 ) * unit );
      ctx.fillText( "2000", ( i % 3 - 1 ) * 330 * unit, ( 300 + Math.floor( i / 3 ) * 220 ) * unit );
    }
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
  }

  /** Times the paint and the forced completion of that paint (drawn into a GPU sink and read back 1 px). */
  private static benchTarget(
    make: () => { canvas: HTMLCanvasElement | OffscreenCanvas; ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D; dispose: () => void } | null,
    w: number,
    h: number,
    scales: number[]
  ): string
  {
    const target = make();
    if ( !target ) return "n/a";
    const rec: number[] = [];
    const flush: number[] = [];
    for ( const scale of scales )
    {
      const sink = document.createElement( "canvas" );
      sink.width = 512;
      sink.height = 512;
      const sink_ctx = sink.getContext( "2d" );
      if ( !sink_ctx ) break;
      const t0 = performance.now();
      this.paintHeavy( target.ctx as CanvasRenderingContext2D, w, h, scale );
      const t1 = performance.now();
      sink_ctx.drawImage( target.canvas as CanvasImageSource, 0, 0, 512, 512 );
      sink_ctx.getImageData( 0, 0, 1, 1 );
      const t2 = performance.now();
      rec.push( t1 - t0 );
      flush.push( t2 - t1 );
      sink.width = 0;
    }
    target.dispose();
    const med = ( list: number[] ) => [ ...list ].sort( ( a, b ) => a - b )[Math.floor( list.length / 2 )] ?? 0;
    return `paint ${med( rec ).toFixed( 1 )} ms, complete ${med( flush ).toFixed( 1 )} ms (runs: ${flush.map( v => v.toFixed( 0 ) ).join( "/" )})`;
  }

  /**
   * Answers two questions on the device: is a detached canvas (the layer
   * caches) rasterised on the GPU like an on-page one, and does thick
   * outlined text get slower when its scale changes every frame (card pop).
   */
  public static canvasBench(): string
  {
    const dpr = Game.getDpr();
    const w = Math.round( Game.getScreenWidth() * dpr );
    const h = Math.round( Game.getScreenHeight() * dpr );
    const fixed = [ 1, 1, 1, 1, 1 ];
    const varying = [ 1.02, 1.05, 1.08, 1.11, 1.14 ];

    const detached = () =>
    {
      const canvas = document.createElement( "canvas" );
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext( "2d" );
      return ctx ? { canvas, ctx, dispose: () => { canvas.width = 0; } } : null;
    };
    const on_page = () =>
    {
      const canvas = document.createElement( "canvas" );
      canvas.width = w;
      canvas.height = h;
      canvas.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;";
      document.body.appendChild( canvas );
      const ctx = canvas.getContext( "2d" );
      return ctx ? { canvas, ctx, dispose: () => { canvas.width = 0; canvas.remove(); } } : null;
    };
    const offscreen = () =>
    {
      if ( typeof OffscreenCanvas === "undefined" ) return null;
      const canvas = new OffscreenCanvas( w, h );
      const ctx = canvas.getContext( "2d" );
      return ctx ? { canvas, ctx, dispose: () => { canvas.width = 0; } } : null;
    };

    const lines = [
      `CANVAS BENCH ${new Date().toISOString()} ${w}x${h} (${( w * h / MPX ).toFixed( 2 )} Mpx) dpr=${dpr.toFixed( 2 )}`,
      ...this.canvasInventory(),
      `detached canvas   : ${this.benchTarget( detached, w, h, fixed )}`,
      `on-page canvas    : ${this.benchTarget( on_page, w, h, fixed )}`,
      `OffscreenCanvas   : ${this.benchTarget( offscreen, w, h, fixed )}`,
      `on-page, text scale changing: ${this.benchTarget( on_page, w, h, varying )}`,
      "  a detached canvas far slower than on-page = it is CPU-rasterised (GPU canvas budget);",
      "  'text scale changing' far slower than on-page = scaling outlined text defeats the glyph cache",
    ];
    const report = lines.join( "\n" );
    this.publish( report );
    return report;
  }

  private static async copy(): Promise<void>
  {
    const text = this.report();
    if ( !text ) return;
    try
    {
      await navigator.clipboard.writeText( text );
      this.flash( "copied" );
      return;
    }
    catch {}
    const area = document.createElement( "textarea" );
    area.value = text;
    area.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
    document.body.appendChild( area );
    area.select();
    const ok = document.execCommand( "copy" );
    area.remove();
    this.flash( ok ? "copied" : "copy failed — text is in console [UIPERF]" );
  }

  private static flash( text: string ): void
  {
    if ( !this.panel_pre ) return;
    const saved = this.panel_pre.textContent;
    this.panel_pre.textContent = `${text}\n\n${saved}`;
    setTimeout( () => { if ( this.panel_pre ) this.panel_pre.textContent = saved; }, 1200 );
  }
}
